import React, { FC, useMemo } from "react";
import { flatten } from "lodash";
import styled, { CSSObject } from "styled-components";

import {
  DropdownMenu,
  MenuGroupHeader,
  MenuItem,
  MultiAutocompleteControl,
  useMultiAutocomplete,
} from "@adsk/alloy-react-dropdown";
import Footer, { ClearButton } from "@adsk/alloy-react-footer";
import { PercentIcon } from "@adsk/alloy-react-icon";
import { useLocale } from "@adsk/alloy-react-locale";
import Portal from "@adsk/alloy-react-portal";
import Theme from "@adsk/alloy-react-theme";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";

import i18n from "../i18n";
import type { CompletionTrackingType } from "../models/completionTracking";
import { isPercentCompleteStatus } from "../models/completionTracking";
import { statusColors, StatusDTO, StatusItem } from "../models/statusSets";
import { ColumnFormField, StatusLabel } from "./commonStyledComponents";
import StatusValueTag from "./StatusValueTag";

const MAX_INPUT_HEIGHT = "160px";

const controlStyle = Object.freeze({
  flexWrap: "wrap",
  maxHeight: MAX_INPUT_HEIGHT,
  overflow: "auto",
} as CSSObject);

// Sits in the same fixed-width slot the dropdown uses for the selected
// checkmark, so the %-badge column is visually aligned with the check column
// (only one of them ever renders for a given row). Mirrors the badge
// styling used by `StatusPicker` to keep the affordance consistent across
// "select a status" and "filter by status" surfaces.
const ProgressBadge = styled.span`
  align-items: center;
  color: ${Theme.colors.charcoal600};
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: 24px;
`;

// Two-line option layout: the colored status label on top, the status set
// name in muted small text below. Lifted from the apps `StatusFilter`
// (`StatusWithStatusSetName`) so reference-picker and apps consumers see
// the same option chrome.
const OptionLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  /* Avoid the secondary line's ellipsis blowing past the badge column. */
  width: calc(100% - 24px);
`;

const StatusSetName = styled.span`
  margin-left: 20px;
  font-size: ${Theme.typography.labelSmall};
  color: ${Theme.colors.charcoal500};
`;

// Structural type for the `statusSets` prop. Loosened from the full
// `StatusSetDTO` so apps consumers (which carry `Date` instances on
// `createdAt`/`updatedAt`/`deletedAt`) can pass their own `StatusSet[]`
// without an explicit cast. Mirrors the `StatusPickerOption` pattern from
// `StatusPicker.tsx`.
export type StatusFilterStatusSet = {
  id: string;
  name: string;
  isDefault?: boolean;
  values: ReadonlyArray<{
    id: string;
    label: string;
    color: string;
    completionTracking?: CompletionTrackingType | null;
    // Per-value active flag. The deleted apps `StatusFilter` filtered
    // individual values with `.filter(({ isActive }) => isActive)` before
    // mapping them; we mirror that here so inactive statuses inside an
    // active status set don't appear in the dropdown. Optional + filtered
    // with `!== false` so existing callers (fixtures, stories, tests) that
    // omit the field continue to behave as "active".
    isActive?: boolean;
  }>;
};

type StatusFilterProps = {
  statusSets: ReadonlyArray<StatusFilterStatusSet>;
  statusIds?: StatusDTO["id"][];
  /**
   * When true (and the option's `completionTracking === "percent_complete"`),
   * the dropdown renders a `%` icon in the option's right slot to hint that
   * picking that status will surface a percent-complete control. Suppressed
   * for already-selected rows (the dropdown's own selected-checkmark sits in
   * the same slot). Owned by the caller so a single LD flag can drive both
   * the asset and reference-picker filter panels through different state
   * stores.
   */
  isPercentCompleteEnabled?: boolean;
  onSelect: (statusId: StatusDTO["id"]) => void;
  onRemove: (statusId: StatusDTO["id"][]) => void;
};

type GroupedStatusItem = {
  label: string;
  items: StatusItem[];
};

const sortAlphabetically = (
  a: StatusFilterStatusSet,
  b: StatusFilterStatusSet
) => a.name.localeCompare(b.name);

const itemToString = (item: StatusItem | null): string => item?.label ?? "";

const StatusFilter: FC<StatusFilterProps> = ({
  statusSets,
  statusIds,
  isPercentCompleteEnabled,
  onSelect,
  onRemove,
}) => {
  const { T } = useLocale(i18n);

  const groupedItems: GroupedStatusItem[] = useMemo(
    () =>
      [...statusSets]
        .sort(sortAlphabetically)
        .map(({ name, values, isDefault, id: statusSetId }) => ({
          label: name,
          // `!== false` (rather than `=== true`) preserves backward compat
          // for callers that don't set the field at all.
          items: values
            .filter(({ isActive }) => isActive !== false)
            .map(
              ({ label, id, color, completionTracking }): StatusItem => ({
                label,
                statusId: id,
                color: color as StatusDTO["color"],
                statusSetName: isDefault
                  ? (T.translate("DEFAULT") as string)
                  : name,
                statusSetId,
                completionTracking,
              })
            ),
        })),
    [T, statusSets]
  );

  const items = useMemo(
    () => flatten(groupedItems.map((group) => group.items)),
    [groupedItems]
  );

  // Tolerant lookup: the apps version of this filter silently drops unknown
  // ids (e.g. a stale URL filter that references a deleted status). The
  // shared-web version used to throw, which crashed reference-picker
  // consumers on the same edge case. Always filter to known ids and trust
  // the caller to reconcile if needed.
  const selectedItems = useMemo(() => {
    if (!statusIds) return [] as StatusItem[];
    const itemById = new Map(items.map((item) => [item.statusId, item]));
    return statusIds
      .map((statusId) => itemById.get(statusId))
      .filter((item): item is StatusItem => Boolean(item));
  }, [items, statusIds]);

  const { controlProps, menuProps } = useMultiAutocomplete<StatusItem>({
    groupedItems,
    selectedItems,
    itemToString,
  });

  const {
    addSelectedItem,
    removeSelectedItem,
    isGroupHeader,
    ...restMenuProps
  } = menuProps;

  const onRemoveSelectedItem = (item: StatusItem) => {
    onRemove([item.statusId]);
    removeSelectedItem(item);
  };

  const onSelectItem = (item: StatusItem) => {
    onSelect(item.statusId);
    addSelectedItem(item);
  };

  const onClear = () => {
    onRemove(statusIds || []);
  };

  const isClearDisabled = Boolean(!selectedItems?.length);

  return (
    <ColumnFormField label={T.translate("statusFilter.label")}>
      <MultiAutocompleteControl
        {...controlProps}
        placeholder={T.translate("statusFilter.placeholder") as string}
        renderItems={({ selectedItems: rendered }) =>
          rendered.map((statusItem) => (
            <StatusValueTag
              {...statusItem}
              key={statusItem.statusId}
              onRemove={() => onRemoveSelectedItem(statusItem)}
            />
          ))
        }
        style={controlStyle}
      />
      {/*
        We need this because we want the dropdown menu to be positioned above
        any neighboring elements, for example, modal footer, other fields, etc.
      */}
      <Portal container={document.body}>
        <DropdownMenu
          {...restMenuProps}
          renderMenuItem={({ item, isSelected, ...itemProps }) => {
            if (isGroupHeader(item)) {
              return (
                <MenuGroupHeader key={item.groupKey} style={{}}>
                  {item.label}
                </MenuGroupHeader>
              );
            }

            const showBadge =
              Boolean(isPercentCompleteEnabled) &&
              isPercentCompleteStatus(item) &&
              !isSelected;

            return (
              <MenuItem
                {...itemProps}
                isSelected={isSelected}
                key={item.statusId}
                data-testid={`StatusFilterOption-${item.statusId}`}
                onClick={() => {
                  const isAlreadySelected = selectedItems.some(
                    (selectedItem) => selectedItem.statusId === item.statusId
                  );
                  const onClickAction = isAlreadySelected
                    ? onRemoveSelectedItem
                    : onSelectItem;

                  onClickAction(item);
                }}
                text={
                  <OptionLayout>
                    <StatusLabel color={statusColors[item.color]}>
                      <OverflowTooltip>{item.label}</OverflowTooltip>
                    </StatusLabel>
                    <StatusSetName>{item.statusSetName}</StatusSetName>
                  </OptionLayout>
                }
                renderRightComponent={
                  showBadge
                    ? () => (
                        <ProgressBadge
                          aria-label={T.translate(
                            "percentCompleteInput.label"
                          )?.toString()}
                          data-testid="StatusFilterProgressBadge"
                        >
                          <PercentIcon width={16} height={16} />
                        </ProgressBadge>
                      )
                    : undefined
                }
              />
            );
          }}
          renderFooter={() => (
            <Footer style={{}}>
              <ClearButton onClick={onClear} disabled={isClearDisabled} />
            </Footer>
          )}
        />
      </Portal>
    </ColumnFormField>
  );
};

export default StatusFilter;
